/**
 * Grouping for the models page.
 *
 * The server computes a category from each model's capabilities and sends it
 * as `category`. This file only names and orders those categories for display
 * -- it does not decide them, because two places deciding would eventually
 * disagree and there would be no way to tell which was right.
 *
 * The fallback exists for one reason: a server that predates the field. It
 * applies the same rule the server does -- the first capability decides, so
 * the catalogue expresses "a coding model that chats" by ordering the list --
 * rather than inventing a second taxonomy.
 */
import type { LocalModel } from './types'

export type ModelCategory =
  | 'CODING'
  | 'CODE_COMPLETION'
  | 'REASONING'
  | 'TEXT_GENERAL'
  | 'VISION'
  | 'MULTIMODAL'
  | 'IMAGE_GENERATION'
  | 'VIDEO_GENERATION'
  | 'TEXT_TO_SPEECH'
  | 'SPEECH_TO_TEXT'
  | 'EMBEDDINGS'
  | 'RERANKING'
  | 'OTHER'

/** Capability to category, matching the server's enum. */
const BY_CAPABILITY: Record<string, ModelCategory> = {
  code: 'CODING',
  'agentic-coding': 'CODING',
  'code-completion': 'CODE_COMPLETION',
  reasoning: 'REASONING',
  chat: 'TEXT_GENERAL',
  vision: 'VISION',
  multimodal: 'MULTIMODAL',
  'image-generation': 'IMAGE_GENERATION',
  'video-generation': 'VIDEO_GENERATION',
  'text-to-speech': 'TEXT_TO_SPEECH',
  'speech-to-text': 'SPEECH_TO_TEXT',
  embedding: 'EMBEDDINGS',
  rerank: 'RERANKING',
}

/** Heading and one line of explanation, so a group says why it is separate. */
export const CATEGORY_LABELS: Record<ModelCategory, { title: string; blurb: string }> = {
  TEXT_GENERAL: { title: 'General', blurb: 'Conversation, writing and everyday questions.' },
  CODING: { title: 'Coding', blurb: 'Tuned for writing, explaining and changing code.' },
  CODE_COMPLETION: {
    title: 'Code completion',
    blurb: 'Trained to fill a gap at the cursor, not just continue from it.',
  },
  REASONING: { title: 'Reasoning', blurb: 'Work through a problem before answering.' },
  VISION: { title: 'Vision', blurb: 'Read images as well as text.' },
  MULTIMODAL: { title: 'Multimodal', blurb: 'Take more than one kind of input at once.' },
  IMAGE_GENERATION: { title: 'Image generation', blurb: 'Make pictures from a description.' },
  VIDEO_GENERATION: { title: 'Video generation', blurb: 'Make short clips from a description.' },
  TEXT_TO_SPEECH: { title: 'Speech', blurb: 'Read text aloud.' },
  SPEECH_TO_TEXT: { title: 'Transcription', blurb: 'Turn recorded audio into text.' },
  EMBEDDINGS: { title: 'Embeddings', blurb: 'Turn text into vectors for search.' },
  RERANKING: { title: 'Reranking', blurb: 'Score documents against a query.' },
  OTHER: { title: 'Other', blurb: 'Everything this build does not recognise yet.' },
}

/** Most-reached-for first; the tail is specialist and rarely browsed. */
export const CATEGORY_ORDER: ModelCategory[] = [
  'TEXT_GENERAL',
  'CODING',
  'CODE_COMPLETION',
  'REASONING',
  'VISION',
  'MULTIMODAL',
  'IMAGE_GENERATION',
  'VIDEO_GENERATION',
  'SPEECH_TO_TEXT',
  'TEXT_TO_SPEECH',
  'EMBEDDINGS',
  'RERANKING',
  'OTHER',
]

export function categoryOf(model: LocalModel): ModelCategory {
  if (model.category && model.category in CATEGORY_LABELS) {
    return model.category as ModelCategory
  }
  // Defended rather than trusted. The type says there is a capability list;
  // the wire does not enforce it, and a caller reaching for a model it does
  // not have yet reads as one with no list at all. Answering "unrecognised"
  // is right in both cases, and is a great deal better than an exception from
  // a function whose whole job is to label a row.
  const first = model.capabilities?.[0]
  return (first && BY_CAPABILITY[first]) || 'OTHER'
}

/**
 * Categories whose models take part in a conversation.
 *
 * Listed rather than excluded, so the question is asked the right way round.
 * The badge these gate says "tools" or "chat only", and that distinction only
 * means anything where a model produces a conversational reply that a tool
 * call could have been part of. An embedding model returns vectors, a
 * reranker returns scores, a transcriber returns what was said, and an image
 * model returns a picture -- for none of them is "chat only" a limitation, it
 * is a category error.
 *
 * <p>Inverting it also decides the unknown case correctly. A category added
 * later, or one this build does not recognise, falls outside this set and
 * gets no badge -- which is right, because the honest answer about a model
 * nobody has classified is to say nothing.
 */
const CONVERSATIONAL: ReadonlySet<ModelCategory> = new Set<ModelCategory>([
  'TEXT_GENERAL',
  'CODING',
  'REASONING',
  'VISION',
  'MULTIMODAL',
])

/**
 * Whether this model answers in a conversation at all.
 *
 * Asked so the models list does not describe a model by what it is not. An
 * image model was being labelled "chat only", which is true and useless: it
 * reads as a limitation of something it was never going to do. Ten more rows
 * had the same label for the same wrong reason -- four embedding models, three
 * transcribers, two rerankers and a voice.
 *
 * Code completion is outside the set deliberately, not by oversight: it is
 * served through `/infill`, which returns the missing middle and has no notion
 * of calling a tool.
 */
export function answersInChat(model: LocalModel): boolean {
  return CONVERSATIONAL.has(categoryOf(model))
}

/** Groups in display order, skipping categories nothing falls into. */
export function groupByCategory(
  models: LocalModel[],
): { category: ModelCategory; models: LocalModel[] }[] {
  const buckets = new Map<ModelCategory, LocalModel[]>()
  for (const model of models) {
    const key = categoryOf(model)
    const existing = buckets.get(key)
    if (existing) existing.push(model)
    else buckets.set(key, [model])
  }
  return CATEGORY_ORDER.filter((c) => buckets.has(c)).map((category) => ({
    category,
    models: buckets.get(category) as LocalModel[],
  }))
}
